import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { formatMarginWithSource, type RateUnit, type RateWarning } from '@clawhire/shared';
import { api, isApiError } from '@/lib/api';
import { formatPct, formatRate } from '@/lib/format';
import { useDebouncedValue } from '@/lib/use-debounce';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { SkeletonLines } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { RateUnitToggle } from './RateUnitToggle';
import { PositionPicker } from './PositionPicker';
import { candidatePath, parseNumber, useInvalidateCandidates } from './api';
import type { CandidateDetail, PositionRef, ProposeResponse, RateCalcResponse } from './types';

export interface ProposeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidate: Pick<
    CandidateDetail,
    'id' | 'candidateId' | 'rateExpectationEurH' | 'rateExpectationText' | 'submissions' | 'computed'
  >;
}

interface PreviewParams {
  position: string | null;
  candidateRate: number | null;
  unit: RateUnit;
  marginPct: number | null;
}

/**
 * "Propose for position": open-position search, candidate rate + unit (prefilled from the rate
 * expectation), optional margin override, a live preview of the suggested offer from
 * `POST /rates/calculate`, then `POST /candidates/:id/propose` and navigation to the submission.
 */
export function ProposeDialog({ open, onOpenChange, candidate }: ProposeDialogProps) {
  const navigate = useNavigate();
  const invalidate = useInvalidateCandidates();
  const [position, setPosition] = React.useState<PositionRef | null>(null);
  const [rate, setRate] = React.useState('');
  const [unit, setUnit] = React.useState<RateUnit>('HOUR');
  const [margin, setMargin] = React.useState('');
  const ids = { rate: React.useId(), margin: React.useId(), position: React.useId() };

  React.useEffect(() => {
    if (open) {
      setPosition(null);
      setRate(candidate.rateExpectationEurH === null ? '' : String(candidate.rateExpectationEurH));
      setUnit('HOUR');
      setMargin('');
    }
  }, [open, candidate.rateExpectationEurH]);

  const params: PreviewParams = {
    position: position?.id ?? null,
    candidateRate: parseNumber(rate),
    unit,
    marginPct: parseNumber(margin),
  };
  const marginInvalid = params.marginPct !== null && (params.marginPct < 0 || params.marginPct >= 100);
  const debouncedKey = useDebouncedValue(JSON.stringify(params), 300);
  const debounced = JSON.parse(debouncedKey) as PreviewParams;

  const preview = useQuery({
    queryKey: ['rates', 'calculate', debounced],
    queryFn: () =>
      api.post<RateCalcResponse>('/rates/calculate', {
        position: debounced.position,
        candidateRate: debounced.candidateRate,
        candidateRateUnit: debounced.unit,
        marginPct: debounced.marginPct ?? undefined,
      }),
    enabled: open && Boolean(debounced.position) && debounced.candidateRate !== null && !marginInvalid,
    staleTime: 30_000,
  });

  const propose = useMutation({
    mutationFn: () =>
      api.post<ProposeResponse>(`${candidatePath(candidate.id)}/propose`, {
        position: position!.id,
        candidateRateEur: params.candidateRate,
        candidateRateUnit: unit,
        marginPct: params.marginPct ?? undefined,
      }),
    onSuccess: async (res) => {
      toast.success(
        `${candidate.computed.displayName} proposed for ${position!.requestNo} ${position!.role}`,
      );
      for (const w of res.warnings ?? []) toast.warning(w.message);
      for (const h of res.hints ?? []) toast.info(h);
      await invalidate();
      onOpenChange(false);
      navigate(`/submissions/${res.id}`);
    },
    onError: (e: Error) => {
      if (isApiError(e) && e.code === 'SUBMISSION_EXISTS') {
        const existing = (e.details as { submissionId?: string } | undefined)?.submissionId;
        toast.error(
          e.message,
          existing
            ? { action: { label: 'Open', onClick: () => navigate(`/submissions/${existing}`) } }
            : undefined,
        );
      } else toast.error(e.message);
    },
  });

  const alreadyProposed = candidate.submissions.map((s) => s.positionId);
  const canSubmit = Boolean(position) && !marginInvalid && !propose.isPending;

  return (
    <Dialog open={open} onOpenChange={(o) => !propose.isPending && onOpenChange(o)}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-2xl">
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) propose.mutate();
          }}
        >
          <DialogHeader>
            <DialogTitle>Propose {candidate.computed.displayName} for a position</DialogTitle>
            <DialogDescription>
              Creates a submission in Screening with the suggested client offer (candidate rate ÷ (1 −
              margin), rounded up).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor={ids.position}>Position</Label>
            <PositionPicker
              id={ids.position}
              value={position}
              onChange={setPosition}
              openOnly
              excludeIds={alreadyProposed}
            />
            {alreadyProposed.length > 0 && !position ? (
              <p className="text-xs text-muted-foreground">
                Positions this candidate is already proposed for are not listed.
              </p>
            ) : null}
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor={ids.rate}>Candidate rate (EUR)</Label>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  id={ids.rate}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="any"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  className="w-32 tabular-nums"
                />
                <RateUnitToggle value={unit} onChange={setUnit} />
              </div>
              {candidate.rateExpectationText ? (
                <p className="text-xs text-muted-foreground">Expectation: {candidate.rateExpectationText}</p>
              ) : null}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor={ids.margin}>Margin override (%)</Label>
              <Input
                id={ids.margin}
                type="number"
                inputMode="decimal"
                min={0}
                max={99.99}
                step="0.01"
                value={margin}
                onChange={(e) => setMargin(e.target.value)}
                placeholder="position / global"
                className="w-32 tabular-nums"
                aria-invalid={marginInvalid}
              />
              <p className="text-xs text-muted-foreground">
                {marginInvalid ? (
                  <span className="text-destructive">Must be between 0 and 99.99.</span>
                ) : (
                  'Leave empty to use the position’s or the global margin.'
                )}
              </p>
            </div>
          </div>

          <OfferPreview
            enabled={Boolean(position) && params.candidateRate !== null}
            loading={preview.isFetching}
            error={
              preview.isError
                ? preview.error instanceof Error
                  ? preview.error.message
                  : 'Preview failed'
                : null
            }
            data={preview.data ?? null}
          />

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={propose.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit} loading={propose.isPending}>
              Propose
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function OfferPreview({
  enabled,
  loading,
  error,
  data,
}: {
  enabled: boolean;
  loading: boolean;
  error: string | null;
  data: RateCalcResponse | null;
}) {
  if (!enabled) {
    return (
      <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
        Choose a position and enter the candidate rate to preview the offer.
      </p>
    );
  }
  if (error) return <p className="text-sm text-destructive">{error}</p>;
  if (!data) return <SkeletonLines lines={3} />;
  const warnings: RateWarning[] = data.warnings ?? [];
  return (
    <section
      aria-label="Suggested offer"
      className={cn('space-y-3 rounded-md border bg-muted/40 p-4', loading && 'opacity-70')}
      aria-busy={loading || undefined}
    >
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Suggested offer to the client
          </p>
          <p className="text-3xl font-semibold tabular-nums" data-testid="suggested-offer">
            {formatRate(data.clientOffer)}
          </p>
        </div>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Candidate</dt>
          <dd className="tabular-nums">{formatRate(data.candidateRateH)}</dd>
          <dt className="text-muted-foreground">Margin</dt>
          <dd className="tabular-nums">{formatMarginWithSource(data.effectiveMargin)}</dd>
          <dt className="text-muted-foreground">Actual margin</dt>
          <dd className="tabular-nums">{formatPct(data.actualMarginPct)}</dd>
          <dt className="text-muted-foreground">Supplier ceiling</dt>
          <dd className="tabular-nums">
            {data.supplierCeiling === null ? (
              <span className="text-muted-foreground">client rate unknown</span>
            ) : (
              `${formatRate(data.supplierCeiling)} (client ${formatRate(data.clientRateH)})`
            )}
          </dd>
        </dl>
      </div>
      {warnings.length > 0 ? (
        <ul className="space-y-1" aria-label="Rate warnings">
          {warnings.map((w) => (
            <li key={w.code} className="flex items-start gap-2 text-sm text-warning">
              <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{w.message}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
